import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.heat";
import { api, BACKEND_URL, getToken } from "../lib/api";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import { notify } from "../lib/notify";
import { MapPin, Flame, Target, Download, RefreshCw, Compass, Ruler } from "lucide-react";

const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors · postcodes.io';
const RING_COLORS = ["#dc2626", "#ef6a4c", "#e0a458", "#6d6a86"];

export default function ReachPage() {
  const { currentEventId, currentEvent } = useEvent();
  const [allTime, setAllTime] = useState(true);
  const [mode, setMode] = useState("pins"); // pins | heat
  const [showRings, setShowRings] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pcInput, setPcInput] = useState("");

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentEventId && !allTime) params.set("event_id", currentEventId);
      if (allTime) params.set("all_time", "true");
      const r = await api.get(`/reach?${params.toString()}`);
      setData(r.data);
      setPcInput(r.data.venue?.postcode || "");
    } catch (e) { notify.error("Could not load reach data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [currentEventId, allTime]);

  const saveVenuePostcode = async () => {
    setBusy(true);
    try {
      await api.patch("/attractions/me", { venue_postcode: pcInput.trim() });
      notify.success("Venue postcode saved");
      await load();
    } catch { notify.error("Could not save postcode"); }
    finally { setBusy(false); }
  };

  const runGeocode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reach/geocode", { limit: 1000 });
      notify.success(`${r.data.newly_geocoded} postcode(s) located${r.data.remaining ? ` · ${r.data.remaining} still to do` : ""}`);
      await load();
    } catch { notify.error("Lookup failed"); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (currentEventId && !allTime) params.set("event_id", currentEventId);
    if (allTime) params.set("all_time", "true");
    const res = await fetch(`${BACKEND_URL}/api/reach/export?${params.toString()}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "reach.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  // ── map setup (imperative Leaflet — no react wrapper needed) ──
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: true, attributionControl: true })
      .setView([53.5, -2.2], 6);
    L.tileLayer(TILES, { attribution: ATTRIB, maxZoom: 18 }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  const points = data?.points || [];
  const venue = data?.venue;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    Object.values(layersRef.current).forEach((l) => l && map.removeLayer(l));
    layersRef.current = {};

    if (mode === "pins" && points.length) {
      const cluster = L.markerClusterGroup({
        showCoverageOnHover: false, maxClusterRadius: 50,
        iconCreateFunction: (c) => {
          const n = c.getAllChildMarkers().reduce((s, m) => s + (m.options.bookings || 1), 0);
          const size = n > 100 ? 54 : n > 25 ? 46 : 38;
          return L.divIcon({
            html: `<div class="reach-cluster" style="width:${size}px;height:${size}px;line-height:${size}px">${n}</div>`,
            className: "reach-cluster-wrap", iconSize: L.point(size, size),
          });
        },
      });
      points.forEach((p) => {
        const r = p.bookings > 20 ? 13 : p.bookings > 5 ? 10 : 7;
        const m = L.circleMarker([p.lat, p.lng], {
          radius: r, color: "#dc2626", weight: 1.5, fillColor: "#dc2626", fillOpacity: 0.55, bookings: p.bookings,
        });
        const orders = (p.orders || []).map((o) =>
          `<a href="/dashboard/orders/${o.id}" class="reach-pop-order">${o.order_number} · ${o.customer_name || "—"}</a>`).join("");
        m.bindPopup(
          `<div class="reach-pop">
            <div class="reach-pop-pc">${p.postcode}${p.approx ? " <span class='reach-pop-approx'>approx</span>" : ""}</div>
            <div class="reach-pop-sub">${p.district || p.outcode}${p.distance_miles != null ? ` · ${p.distance_miles} miles away` : ""}</div>
            <div class="reach-pop-stat">${p.bookings} booking${p.bookings === 1 ? "" : "s"} · ${p.tickets} ticket${p.tickets === 1 ? "" : "s"}</div>
            <div class="reach-pop-orders">${orders}</div>
          </div>`, { maxWidth: 260 });
        cluster.addLayer(m);
      });
      cluster.addTo(map);
      layersRef.current.cluster = cluster;
    }

    if (mode === "heat" && points.length) {
      const max = Math.max(...points.map((p) => p.bookings));
      const heat = L.heatLayer(points.map((p) => [p.lat, p.lng, Math.max(0.35, p.bookings / max)]), {
        radius: 34, blur: 26, maxZoom: 11, minOpacity: 0.35,
        gradient: { 0.2: "#3b2fa8", 0.45: "#8b5cf6", 0.65: "#e0a458", 0.85: "#ef6a4c", 1: "#dc2626" },
      }).addTo(map);
      layersRef.current.heat = heat;
    }

    if (venue) {
      const vm = L.marker([venue.lat, venue.lng], {
        icon: L.divIcon({ html: `<div class="reach-venue"></div>`, className: "", iconSize: [18, 18] }),
      }).bindPopup(`<div class="reach-pop"><div class="reach-pop-pc">Your venue</div><div class="reach-pop-sub">${venue.postcode}</div></div>`).addTo(map);
      layersRef.current.venue = vm;
      if (showRings) {
        const grp = L.layerGroup();
        [10, 25, 50, 100].forEach((mi, i) => {
          L.circle([venue.lat, venue.lng], {
            radius: mi * 1609.34, color: RING_COLORS[i], weight: 1, opacity: 0.45, fill: false, dashArray: "4 6",
          }).addTo(grp);
          L.marker([venue.lat + (mi * 1609.34) / 111320, venue.lng], {
            icon: L.divIcon({ html: `<div class="reach-ring-label">${mi} mi</div>`, className: "", iconSize: [40, 14] }),
            interactive: false,
          }).addTo(grp);
        });
        grp.addTo(map);
        layersRef.current.rings = grp;
      }
    }

    if (points.length) {
      const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      if (venue) b.extend([venue.lat, venue.lng]);
      map.fitBounds(b.pad(0.2), { maxZoom: 10 });
    }
  }, [data, mode, showRings]); // eslint-disable-line

  const cov = data?.coverage;
  const maxArea = useMemo(() => Math.max(1, ...(data?.top_areas || []).map((x) => x.bookings)), [data]);
  const maxRing = useMemo(() => Math.max(1, ...(data?.rings || []).map((x) => x.bookings)), [data]);

  return (
    <DashboardLayout title="Reach" subtitle="Where your audience travels from"
      actions={
        <div className="flex items-center gap-2">
          <button className="st-btn st-btn-ghost text-sm" onClick={exportCsv} data-testid="reach-export"><Download size={14} /> Export</button>
          <button className="st-btn st-btn-ghost text-sm" onClick={runGeocode} disabled={busy} data-testid="reach-geocode"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh locations</button>
        </div>
      }>
      <div className="space-y-5" data-testid="reach-page">
        {/* controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="st-seg" data-testid="reach-scope">
            <button className={`st-seg-btn ${allTime ? "is-active" : ""}`} onClick={() => setAllTime(true)} data-testid="reach-scope-all">All time</button>
            <button className={`st-seg-btn ${!allTime ? "is-active" : ""}`} onClick={() => setAllTime(false)} disabled={!currentEventId} data-testid="reach-scope-event">
              {currentEvent?.name ? currentEvent.name.slice(0, 22) : "This event"}
            </button>
          </div>
          <div className="st-seg" data-testid="reach-mode">
            <button className={`st-seg-btn ${mode === "pins" ? "is-active" : ""}`} onClick={() => setMode("pins")} data-testid="reach-mode-pins"><MapPin size={13} /> Pins</button>
            <button className={`st-seg-btn ${mode === "heat" ? "is-active" : ""}`} onClick={() => setMode("heat")} data-testid="reach-mode-heat"><Flame size={13} /> Heatmap</button>
          </div>
          <button className={`st-btn st-btn-ghost text-sm ${showRings ? "text-white" : "text-[#8a8a8a]"}`} onClick={() => setShowRings((v) => !v)} data-testid="reach-toggle-rings">
            <Target size={14} /> {showRings ? "Hide" : "Show"} distance rings
          </button>
        </div>

        {/* venue postcode prompt */}
        {data && !data.venue_postcode_set && (
          <div className="rounded-xl border border-[#3a2a0a] bg-[#1a1205] p-4" data-testid="reach-venue-prompt">
            <div className="text-sm text-[#e8c987] mb-2 flex items-center gap-2"><Compass size={14} /> Add your venue postcode to unlock travel distances and catchment rings.</div>
            <div className="flex flex-wrap gap-2">
              <input className="st-input max-w-[200px]" placeholder="e.g. LN1 1AA" value={pcInput}
                     onChange={(e) => setPcInput(e.target.value)} data-testid="reach-venue-postcode-input" />
              <button className="st-btn" onClick={saveVenuePostcode} disabled={busy || !pcInput.trim()} data-testid="reach-venue-postcode-save">Save</button>
            </div>
          </div>
        )}

        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reach-stats">
          <Stat label="Postcodes on the map" value={data?.stats?.unique_postcodes ?? "—"} testid="reach-stat-postcodes" />
          <Stat label="Bookings mapped" value={data?.stats?.mapped_bookings ?? "—"} testid="reach-stat-bookings" />
          <Stat label="Average travel" value={data?.stats?.avg_miles != null ? `${data.stats.avg_miles} mi` : "—"} icon={Ruler} testid="reach-stat-avg" />
          <Stat label="Furthest visitor" value={data?.stats?.max_miles != null ? `${data.stats.max_miles} mi` : "—"} testid="reach-stat-max" />
        </div>

        {/* map */}
        <div className="st-card p-0 overflow-hidden relative">
          <div ref={mapEl} className="reach-map" data-testid="reach-map" />
          {loading && <div className="absolute inset-0 grid place-items-center bg-[#0a0a0ab0] text-sm text-[#bbb]">Loading map…</div>}
          {!loading && points.length === 0 && (
            <div className="absolute inset-0 grid place-items-center bg-[#0a0a0ab0] text-sm text-[#8a8a8a] text-center px-6" data-testid="reach-empty">
              No mapped postcodes yet for this view.<br />Postcodes collected at checkout will appear here.
            </div>
          )}
        </div>

        {/* coverage note */}
        {cov && (
          <div className="text-xs text-[#8a8a8a]" data-testid="reach-coverage">
            Mapping {cov.with_postcode} of {cov.orders_total} bookings ({cov.percent}%) across {cov.geocoded_postcodes} postcode
            {cov.geocoded_postcodes === 1 ? "" : "s"}.
            {cov.pending_geocode > 0 && <> {cov.pending_geocode} postcode(s) not looked up yet — hit <b>Refresh locations</b>.</>}
            {cov.unresolvable > 0 && <> {cov.unresolvable} couldn't be matched to a UK location.</>}
          </div>
        )}

        {/* rings + top areas */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="st-card">
            <h3 className="text-xs uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Catchment</h3>
            {!data?.rings?.length ? (
              <p className="text-sm text-[#7a7a7a]">Add your venue postcode to see how far people travel.</p>
            ) : (
              <div className="space-y-2" data-testid="reach-rings">
                {data.rings.map((r) => (
                  <div key={r.label} className="flex items-center gap-3 text-sm" data-testid={`reach-ring-${r.max_miles || "far"}`}>
                    <span className="w-20 text-[#bbb] shrink-0">{r.label}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-[#141414] overflow-hidden">
                      <div className="h-full rounded-full bg-[#dc2626]" style={{ width: `${(r.bookings / maxRing) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right text-[#8a8a8a]">{r.bookings}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="st-card">
            <h3 className="text-xs uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Top areas</h3>
            {!data?.top_areas?.length ? <p className="text-sm text-[#7a7a7a]">Nothing to show yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reach-top-areas">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
                    <tr><th className="py-2">Area</th><th>Bookings</th><th>Tickets</th><th>Avg travel</th></tr>
                  </thead>
                  <tbody>
                    {data.top_areas.map((r) => (
                      <tr key={r.area} className="border-t border-[#1a1a1a]" data-testid={`reach-area-row`}>
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span className="h-1.5 rounded-full bg-[#dc2626]" style={{ width: `${Math.max(6, (r.bookings / maxArea) * 60)}px` }} />
                            {r.area || "—"}
                          </div>
                        </td>
                        <td>{r.bookings}</td>
                        <td>{r.tickets}</td>
                        <td className="text-[#8a8a8a]">{r.avg_miles != null ? `${r.avg_miles} mi` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="st-card py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#7a7a7a] flex items-center gap-1">{Icon && <Icon size={11} />}{label}</div>
      <div className="text-2xl mt-1">{value}</div>
    </div>
  );
}
